/** B3 — types + helpers shared by TasksPage and ReconWizardPage. */

import { apiFetch } from './api'

export const RECON_TASK_TYPES = [
  'position_trade_missing',
  'position_balance_stale',
  // mig 157 — the derived-balance OFFER. Renders on the same reconcile
  // card; it is not a break (nothing disagrees, the balance is merely
  // older than the movements).
  'position_balance_rollforward',
  'position_leg_identity_violation',
  'instrument_trade_missing',
  'instrument_balance_stale',
  'commitment_supply_mismatch',
] as const

export type ReconTaskType = (typeof RECON_TASK_TYPES)[number]

export function isReconTaskType(t: string): t is ReconTaskType {
  return (RECON_TASK_TYPES as readonly string[]).includes(t)
}

/** Document-request follow-up task types. ``request_overdue`` is fired
 *  by the request-overdue listener when a sent DR passes its deadline;
 *  its ``target_id`` is the DocumentRequest id (``target_type =
 *  'document_request'``). The officer views the underlying request and
 *  escalates it (the only forward transition the status API exposes for
 *  an overdue request — see ``escalateRequest``). */
export const DOCUMENT_REQUEST_TASK_TYPES = ['request_overdue'] as const

export type DocumentRequestTaskType = (typeof DOCUMENT_REQUEST_TASK_TYPES)[number]

export function isDocumentRequestTaskType(t: string): t is DocumentRequestTaskType {
  return (DOCUMENT_REQUEST_TASK_TYPES as readonly string[]).includes(t)
}

/** Manager-verification task types. Created by the verification-signoff /
 *  verification-remediation listeners when a fund crosses (or loses) the
 *  100% verification threshold; ``target_type = 'fund'``,
 *  ``target_id = fund_id``. Surfaced to managers / admins for a
 *  single Approve per task. */
export const VERIFICATION_TASK_TYPES = ['verification_signoff', 'verification_remediation'] as const

export type VerificationTaskType = (typeof VERIFICATION_TASK_TYPES)[number]

export function isVerificationTaskType(t: string): t is VerificationTaskType {
  return (VERIFICATION_TASK_TYPES as readonly string[]).includes(t)
}

/** Task types whose UI surface is **deferred post-pilot** (Ale 2026-06-08).
 *  Backend continues to create these task rows (cron + listener stay live so
 *  state accumulates and is ready to wire when the UI ships), but the
 *  officer queue hides them so a pilot user does not mistake an unfinished
 *  surface for a working feature. Today: ``daily_digest`` (M-037
 *  auto-confirm retrospective review). Audit cut 1 (2026-09-04) extends
 *  the same rule to the v2 workbench queue, the landing tile and the Other
 *  Ops card set (v2/workbench/api.ts) — the rows keep minting and closing
 *  server-side, unseen. To revert: remove the entry below; the QueueCard /
 *  ReconWizardPage paths already handle the type generically. */
export const DEFERRED_TASK_TYPES = ['daily_digest'] as const

export type DeferredTaskType = (typeof DEFERRED_TASK_TYPES)[number]

export function isDeferredTaskType(t: string): t is DeferredTaskType {
  return (DEFERRED_TASK_TYPES as readonly string[]).includes(t)
}

/** Roles allowed to approve a manager-verification task. The pilot
 *  champion is seeded as ``manager`` (renamed from the old senior-officer
 *  role, PR #367); ``admin`` also qualifies. */
export const VERIFICATION_APPROVER_ROLES = ['manager', 'admin'] as const

export function canApproveVerification(role: string | undefined): boolean {
  return role !== undefined && (VERIFICATION_APPROVER_ROLES as readonly string[]).includes(role)
}

export interface TaskQueueItem {
  task_id: string
  task_type: string
  priority: 'urgent' | 'high' | 'normal' | 'low' | string
  status: string
  title: string
  description: string | null
  target_type: string | null
  target_id: string | null
  fund_id: string
  fund_name: string
  task_data: ReconTaskData | Record<string, unknown> | null
  created_at: string
}

export interface TaskQueueResponse {
  items: TaskQueueItem[]
  generated_at: string
}

/** Ale ruling 2026-07-26: the break's suggested cause, derived from which
 *  side is more recent, with the evidence that justifies it. `cause` is
 *  `undeterminable` whenever the engine could not honestly decide (same-day
 *  dates, a missing date, or a remedy that would contradict the record's own
 *  sign rules) — the pane must then offer both calls without a suggestion. */
export interface BreakDiagnosis {
  cause: 'missing_trade' | 'stale_balance' | 'undeterminable'
  reason: string
  stored: string
  computed: string
  delta: string
  balance_as_of: string | null
  latest_trade_date: string | null
  suggested_units_delta: string | null
}

export interface ReconTaskData {
  target_kind?: 'position' | 'instrument' | string
  target_id?: string
  expected_units_delta?: string
  latest_trade_effective_date?: string | null
  balance_update_date?: string | null
  diagnosis?: BreakDiagnosis
}

/** The four balance-vs-trades breaks an officer can settle with a
 *  confirm/override call. `commitment_supply_mismatch` and
 *  `position_leg_identity_violation` compare two STORED figures — there is
 *  no balance-versus-movements question to answer on them — so they are
 *  deliberately excluded. Mirrors `nolos.recon.break_resolution
 *  .RESOLVABLE_BREAK_TYPES`. */
export const RESOLVABLE_BREAK_TYPES = [
  'position_trade_missing',
  'position_balance_stale',
  'instrument_trade_missing',
  'instrument_balance_stale',
  // mig 157 — settled by the SAME verb on the SAME `stale_balance`
  // outcome, though it is an offer rather than a break. Mirrors
  // `nolos.recon.break_resolution.RESOLVABLE_TASK_TYPES`.
  'position_balance_rollforward',
] as const

export function isResolvableBreak(taskType: string): boolean {
  return (RESOLVABLE_BREAK_TYPES as readonly string[]).includes(taskType)
}

/** Shape of a DocumentRequest as returned by
 *  ``GET /api/funds/{fund_id}/requests`` (DocumentRequestResponse). Only
 *  the fields the queue view renders are typed; the API returns more. */
export interface DocumentRequest {
  id: string
  fund_id: string
  counterparty_name: string
  status: 'draft' | 'sent' | 'overdue' | 'escalated' | 'fulfilled' | 'cancelled' | string
  priority?: string | null
  deadline?: string | null
  sent_at?: string | null
  recipient_email?: string | null
  created_at: string
  updated_at: string
}

/** WS-B #4 — one rolled-up officer evidence to-do, as returned by
 *  ``GET /api/funds/{fund_id}/evidence-rollup`` (EvidenceRollupItem). All
 *  open work for a single (target, evidential_purpose) collapsed into one
 *  line with slot progress; ``gap_ids`` / ``approval_task_ids`` preserve
 *  the 1:1 tracking underneath. */
export interface EvidenceRollupItem {
  fund_id: string
  target_type: string
  target_id: string
  target_label: string | null
  evidential_purpose: string
  label: string
  is_mandatory: boolean
  slots_total: number
  slots_collected: number
  satisfied_count: number
  pending_signoff_count: number
  open_request_count: number
  gap_ids: string[]
  approval_task_ids: string[]
}

export function fetchEvidenceRollup(fundId: string): Promise<EvidenceRollupItem[]> {
  return apiFetch<EvidenceRollupItem[]>(`/api/funds/${encodeURIComponent(fundId)}/evidence-rollup`)
}

export interface SendImpliedTradeResponse {
  task_id: string
  document_request_id: string
  status: string
  deadline: string
  sent_at: string
}

export interface ConfirmPreExistingResponse {
  task_id: string
  trade_id: string
  cancelled_document_request_id: string | null
}

export const PRIORITY_RANK: Record<string, number> = {
  urgent: 0,
  high: 1,
  normal: 2,
  low: 3,
}

export const PRIORITY_LABEL: Record<string, string> = {
  urgent: 'URGENT',
  high: 'HIGH',
  normal: 'NORMAL',
  low: 'LOW',
}

export const PRIORITY_BAR_COLOR: Record<string, string> = {
  urgent: 'bg-orange-500',
  high: 'bg-amber-500',
  normal: 'bg-slate-300',
  low: 'bg-slate-200',
}

export function priorityBarColor(priority: string): string {
  return PRIORITY_BAR_COLOR[priority] ?? 'bg-slate-200'
}

/** Human-friendly label for the closed task_type enum. */
export function taskTypeLabel(t: string): string {
  return t.replace(/_/g, ' ').toUpperCase()
}

// ── Work-item taxonomy (type → subtype) — WS-C ───────────────────────
//
// The unified officer surface (EventsTab) sorts/filters every event-
// triggered input by a small set of *types*, each with declinations
// (subtypes). Source of truth: the design-doc taxonomy table in
// ``thoughts/plans/officer-work-surface-and-verification-gate.md``.
//
//   Record         — accuracy gate. proposal-group reviews + the
//                    informational digest (no-decision, lowest priority).
//   Evidence       — legal gate. approval sign-off / aged-renewal /
//                    the gap lifecycle. Flagged mandatory vs optional.
//   Reconciliation — the four recon primitives.
//   Request        — chasing documents (overdue).
//   Verification   — the one manager sign-off (+ remediation, kept live).
//   System         — pipeline errors: document_stuck / dead_letter_review
//                    (retry / discard).

// TAX (2026-06-30): the ``chain_boundary`` kind was dissolved — look-through
// folds into Record. ``fund_setup`` RETIRED (Ale ruling 2026-07-24, mig 141):
// the fund/root-entity conflict rides the standard possible_match question
// on the pending confirm_chain_head proposal, not a bespoke task.
export type WorkItemType =
  'record' | 'evidence' | 'reconciliation' | 'request' | 'verification' | 'system'

export interface TaxonomyEntry {
  /** Top-level work-item type. */
  type: WorkItemType
  /** Machine subtype key (declination within the type). */
  subtype: string
}

/** task_type → (type, subtype). Every task_type in the DB CHECK set at
 *  mig 141 is mapped (arch audit 2026-07 §3 closed the v1/v2 drift);
 *  dropped types are intentionally absent. */
export const TASK_TYPE_TAXONOMY: Record<string, TaxonomyEntry> = {
  // Record (accuracy)
  proposal_review: { type: 'record', subtype: 'record' },
  daily_digest: { type: 'record', subtype: 'informational' },
  // Look-through review folds into Record (chain_boundary dissolved, mirrors
  // work_items/adapter.py).
  look_through_review: { type: 'record', subtype: 'look_through' },
  source_question: { type: 'record', subtype: 'source_question' },
  // Evidence (legal)
  evidence_approval: { type: 'evidence', subtype: 'approval' },
  investigation_action: { type: 'evidence', subtype: 'gap' },
  // Reconciliation
  trade_duplicate_question: { type: 'reconciliation', subtype: 'trade_duplicate' },
  position_trade_missing: { type: 'reconciliation', subtype: 'position_missing_trade' },
  position_balance_stale: { type: 'reconciliation', subtype: 'position_stale_balance' },
  position_balance_rollforward: {
    type: 'reconciliation',
    subtype: 'position_balance_rollforward',
  },
  position_leg_identity_violation: { type: 'reconciliation', subtype: 'position_leg_identity' },
  instrument_trade_missing: { type: 'reconciliation', subtype: 'instrument_missing_trade' },
  instrument_balance_stale: { type: 'reconciliation', subtype: 'instrument_stale_balance' },
  commitment_supply_mismatch: { type: 'reconciliation', subtype: 'commitment_supply_mismatch' },
  // Request
  request_overdue: { type: 'request', subtype: 'overdue' },
  // B7 (mig 139): encrypted-terminal upload → officer chases an unlocked
  // copy out-of-band; closes via /tasks/{id}/close.
  request_unlocked_copy: { type: 'request', subtype: 'unlocked_copy' },
  // Verification (manager)
  verification_signoff: { type: 'verification', subtype: 'signoff' },
  verification_remediation: { type: 'verification', subtype: 'remediation' },
  // System (pipeline error)
  document_stuck: { type: 'system', subtype: 'document_stuck' },
  dead_letter_review: { type: 'system', subtype: 'dead_letter_review' },
  // v1/v2 map drift closed (arch audit 2026-07 §3): the remaining CHECK-set
  // types the v1 taxonomy did not know rendered as "Record/unknown". The
  // chain_check reminders auto-resolve (no officer verb; the backend
  // answers both generic verbs with an honest message); backfill_review is
  // the legacy migration-040 bucket; evidence_aged is kept in the CHECK for
  // legacy rows only (TAX-2 dropped its creator).
  chain_orphan: { type: 'record', subtype: 'chain_orphan' },
  look_through_incomplete: { type: 'record', subtype: 'look_through_incomplete' },
  backfill_review: { type: 'record', subtype: 'backfill_review' },
  evidence_aged: { type: 'evidence', subtype: 'aged' },
}

/** Proposal groups surfaced from ``/api/proposals`` are Record work
 *  items (the accuracy gate) — they have no task_type of their own. */
export const PROPOSAL_GROUP_WORK_ITEM_TYPE: WorkItemType = 'record'

export const WORK_ITEM_TYPE_LABEL: Record<WorkItemType, string> = {
  record: 'Record',
  evidence: 'Evidence',
  reconciliation: 'Reconciliation',
  request: 'Request',
  verification: 'Verification',
  system: 'System',
}

/** Display / grouping order, mirroring the design-doc table. Also the
 *  tie-break order when sorting by type. */
export const WORK_ITEM_TYPE_ORDER: readonly WorkItemType[] = [
  'record',
  'evidence',
  'reconciliation',
  'request',
  'verification',
  'system',
] as const

export const WORK_ITEM_TYPE_RANK: Record<WorkItemType, number> = WORK_ITEM_TYPE_ORDER.reduce(
  (acc, t, i) => {
    acc[t] = i
    return acc
  },
  {} as Record<WorkItemType, number>
)

/** Fallback for any task_type not in the taxonomy (e.g. a value added by
 *  a future migration before the FE map catches up). Treated as Record
 *  so it still surfaces rather than silently vanishing. */
const UNKNOWN_TAXONOMY: TaxonomyEntry = { type: 'record', subtype: 'unknown' }

export function taxonomyForTaskType(t: string): TaxonomyEntry {
  return TASK_TYPE_TAXONOMY[t] ?? UNKNOWN_TAXONOMY
}

export function workItemType(t: string): WorkItemType {
  return taxonomyForTaskType(t).type
}

export function workItemSubtype(t: string): string {
  return taxonomyForTaskType(t).subtype
}

/** Human-friendly subtype label, e.g. ``position_missing_trade`` →
 *  "position · missing trade". */
export function workItemSubtypeLabel(t: string): string {
  return taxonomyForTaskType(t)
    .subtype.replace(/^(position|instrument)_/, '$1 · ')
    .replace(/_/g, ' ')
}

/** System (pipeline-error) task types — retry / discard via the
 *  stuck-action endpoint. */
export const SYSTEM_TASK_TYPES = ['document_stuck', 'dead_letter_review'] as const

export function isSystemTaskType(t: string): boolean {
  return workItemType(t) === 'system'
}

/** Whether an Evidence work item is *mandatory* — WS-B #2 records this on
 *  the ``evidence_approval`` task_data (``is_mandatory`` / ``blocking``)
 *  and reflects it in priority (mandatory ⇒ ``high``, optional ⇒ ``low``).
 *  Mandatory evidence blocks the fund's verification % and ranks higher;
 *  optional is shown low-priority and never blocks. */
export function isMandatoryEvidence(task: TaskQueueItem): boolean {
  if (workItemType(task.task_type) !== 'evidence') return false
  const d = task.task_data as Record<string, unknown> | null
  if (!d) return false
  return Boolean(d.is_mandatory ?? d.blocking)
}

/** Sort mode for the unified surface. */
export type WorkItemSort = 'priority' | 'oldest' | 'newest'

export interface TaskFilter {
  /** Restrict to one work-item type, or ``'all'``. */
  type?: WorkItemType | 'all'
  sort?: WorkItemSort
}

/** The set of work-item types present among a list of tasks (deferred
 *  types excluded) — drives which filter chips to render. */
export function presentWorkItemTypes(tasks: readonly TaskQueueItem[]): WorkItemType[] {
  const present = new Set<WorkItemType>()
  for (const t of tasks) {
    if (isDeferredTaskType(t.task_type)) continue
    present.add(workItemType(t.task_type))
  }
  return WORK_ITEM_TYPE_ORDER.filter((t) => present.has(t))
}

/** Pure filter + sort for the unified surface. Always drops
 *  deferred-surface task types (e.g. ``daily_digest``). ``priority`` sort
 *  ranks by priority then newest-first (the established queue order);
 *  ``oldest`` / ``newest`` sort purely by age. Kept pure so it is unit-
 *  testable without React. */
export function filterAndSortTasks(
  tasks: readonly TaskQueueItem[],
  { type = 'all', sort = 'priority' }: TaskFilter = {}
): TaskQueueItem[] {
  const filtered = tasks.filter((t) => {
    if (isDeferredTaskType(t.task_type)) return false
    if (type !== 'all' && workItemType(t.task_type) !== type) return false
    return true
  })

  const byAge = (a: TaskQueueItem, b: TaskQueueItem, newestFirst: boolean) => {
    const cmp = a.created_at.localeCompare(b.created_at)
    return newestFirst ? -cmp : cmp
  }

  return filtered.sort((a, b) => {
    if (sort === 'oldest') return byAge(a, b, false)
    if (sort === 'newest') return byAge(a, b, true)
    // 'priority': rank then newest-first.
    const ra = PRIORITY_RANK[a.priority] ?? 99
    const rb = PRIORITY_RANK[b.priority] ?? 99
    if (ra !== rb) return ra - rb
    return byAge(a, b, true)
  })
}

/** Whether the task supports the implied-trade-document-request actions
 *  (Send DR + Confirm pre-existing). Today: only the *_trade_missing
 *  primitives. *_balance_stale tasks only support manual-edit. */
export function supportsImpliedTradeActions(taskType: string): boolean {
  return taskType === 'position_trade_missing' || taskType === 'instrument_trade_missing'
}

/** Default SLA for a freshly-staged DocumentRequest (Ale 2026-06-02 lock).
 *  Officer-amendable per send via the Send-DR input. */
export const DEFAULT_SLA_DAYS = 5

// ── Queue-action API helpers ─────────────────────────────────────────

/** Fetch a single DocumentRequest by id for a fund. There is no
 *  ``GET /api/requests/{id}`` today, so we list the fund's requests and
 *  pick the row out client-side. At pilot scale (≤ a few hundred DRs per
 *  fund) this is cheap; swap for a dedicated GET if it ever grows. */
export async function fetchDocumentRequest(
  fundId: string,
  requestId: string
): Promise<DocumentRequest | null> {
  const rows = await apiFetch<DocumentRequest[]>(`/api/funds/${fundId}/requests`)
  return rows.find((r) => r.id === requestId) ?? null
}

/** Escalate an overdue document request. Maps to
 *  ``PATCH /api/requests/{id}/status`` with ``{status: 'escalated'}`` —
 *  the only forward transition the status API exposes for an
 *  ``overdue`` request (the backend has no cancel/close transition on
 *  this endpoint; see VALID_REQUEST_TRANSITIONS server-side). */
export async function escalateRequest(requestId: string): Promise<DocumentRequest> {
  return apiFetch<DocumentRequest>(`/api/requests/${requestId}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status: 'escalated' }),
  })
}

/** Approve (resolve) a manager-verification task via
 *  ``POST /api/tasks/{id}/resolve``. Backend accepts
 *  verification_signoff / verification_remediation (manager minimum)
 *  plus look_through_review / evidence_approval /
 *  trade_duplicate_question (ops_officer). */
export async function approveVerificationTask(
  taskId: string
): Promise<{ task_id: string; status: string }> {
  return apiFetch<{ task_id: string; status: string }>(`/api/tasks/${taskId}/resolve`, {
    method: 'POST',
    body: JSON.stringify({}),
  })
}
